import { useMemo } from 'react';

import { useOAuthSession } from '@/features/auth';
// deep import (not the feature barrel) to avoid a trading<->custodial import cycle.
import { useAgentReactivation } from '@/features/trading/hooks/useAgentReactivation';
// the store module, not `useAuth`: this only needs to read which authority is
// live, and useAuth writes the runtime mode as a side effect of being called.
import { useTradingSessionStore } from '@/features/tradingSession/store';

import {
    isActivationFundingRequired,
    useActivationFundingBlock,
} from '../snappyActivation';

import { useCustodialBalance } from './useCustodialBalance';

/**
 * The single lifecycle state of the user's Snappy Wallet. One backend condition
 * used to fan out into four independently-gated panels with three different
 * vocabularies; this union is the one thing every surface reads instead.
 *
 * Ordering matters: an earlier status wins over a later one, because the
 * earlier one is the step that actually blocks the user.
 */
export type SnappyWalletState =
    /** S1 — demo mode: there is no backend wallet to talk about. */
    | { status: 'inactive' }
    /** S2a — real mode, nobody signed in, no wallet: any sign-in is the way in. */
    | { status: 'signInRequired' }
    /**
     * S2b — real mode, signed in, no backend wallet row yet: the first deposit
     * provisions it. `cohort` picks the copy: a 'social' user is told their
     * wallet is created from their account (with `displayName` when known); a
     * 'wallet' user (eip712 / passkey authority, no social identity) already has
     * both custodial wallets on chain, and the deposit only activates them, so
     * `displayName` is always null and nothing may mention a social login.
     */
    | {
          status: 'firstDepositRequired';
          cohort: 'social' | 'wallet';
          displayName: string | null;
      }
    /** S5 — wallet exists but the Hyperliquid agent (or the builder fee) needs re-approval. */
    | {
          status: 'reactivationRequired';
          builderApprovalStale: boolean;
          /**
           * True when reactivation CANNOT succeed yet: the HyperCore bootstrap still has to run and
           * it deposits USDC out of the custodial wallet, so agent setup fails with
           * HIP3_SETUP_FUNDING_REQUIRED below the minimum. Older backends only discover that inside
           * the background job, after the reactivate POST already answered `queued`, so the button
           * would look like it did nothing. Ask for the deposit instead.
           */
          fundingRequired: boolean;
      }
    /** S3 — wallet exists and is live, but holds nothing. */
    | { status: 'unfunded' }
    /** S4 — wallet active and funded. */
    | { status: 'ready' };

/**
 * True for the two states that own the onboarding prompt (S2a/S2b). Every other
 * "deposit to start" surface must stay silent while this is true, so the user
 * sees exactly one instruction.
 */
export function isSnappyOnboarding(state: SnappyWalletState): boolean {
    return (
        state.status === 'signInRequired' ||
        state.status === 'firstDepositRequired'
    );
}

/**
 * Joins the social session, `GET /speed/balance` and `GET /speed/agent/status`
 * into one Snappy Wallet lifecycle state. Panels branch on the resulting status
 * rather than each re-deriving "does this user have a wallet?" from a different
 * signal.
 */
export function useSnappyWalletState(): SnappyWalletState {
    const { isAuthenticated: isSocialAuthed, displayName } = useOAuthSession();
    // 'eip712' / 'passkey' is the wallet-first cohort: they signed in as an owner
    // EOA and created both custodial wallets themselves during onboarding.
    const authorityMethod = useTradingSessionStore(
        (state) => state.status?.method ?? null,
    );
    const {
        isCustodialWalletMissing,
        isLoading: isBalanceLoading,
        numericBalance,
    } = useCustodialBalance();
    const {
        isRealMode,
        needsReactivation,
        walletMissing,
        builderApprovalStale,
        bootstrapRequired,
        agentAddress,
        validUntil,
    } = useAgentReactivation();
    // set when the backend refused or failed an activation for want of funds; the status read alone
    // would happily say "reactivate" again.
    const isFundingBlockedByBackend = useActivationFundingBlock(
        (state) => state.isBlocked,
    );

    return useMemo<SnappyWalletState>(() => {
        // demo mode never has a backend wallet; every panel already gated on this.
        if (!isRealMode) {
            return { status: 'inactive' };
        }

        // two endpoints report the same condition — the agent status flag and the balance
        // endpoint's specific `custodialWalletMissing` code. never a generic isError: a
        // transient balance failure must not push a funded user back into onboarding.
        if (walletMissing || isCustodialWalletMissing) {
            if (isSocialAuthed) {
                return {
                    status: 'firstDepositRequired',
                    cohort: 'social',
                    displayName,
                };
            }
            // the wallet cohort only reaches real mode with a minted trading authority, and
            // RustSigner mints one only after verifying both custodial wallets exist on chain.
            // So "missing" here is the backend's speedtrading_wallets row, which nothing but
            // the first deposit (or trade) creates: the same "one deposit away" the social
            // cohort gets, never a setup to finish and never a social sign-in to start.
            if (authorityMethod === 'eip712' || authorityMethod === 'passkey') {
                return {
                    status: 'firstDepositRequired',
                    cohort: 'wallet',
                    displayName: null,
                };
            }
            return { status: 'signInRequired' };
        }

        // outranks 'unfunded': re-approval blocks trading regardless of the balance, and the
        // banner already renders next to (not instead of) the trade form.
        if (needsReactivation) {
            // only when the bootstrap is genuinely still part of the run (the backend says so, or the
            // agent has never been approved at all): an already-approved agent re-approves fine on a
            // zero custodial balance and must not be told to deposit.
            const fundingRequired = isActivationFundingRequired({
                agentAddress,
                validUntil,
                bootstrapRequired,
                // reaching this branch means the status read answered: `needsReactivation` came from it.
                agentStatusKnown: true,
                numericBalance,
                isBalanceLoading,
                fundingBlockedByBackend: isFundingBlockedByBackend,
            });
            return {
                status: 'reactivationRequired',
                builderApprovalStale,
                fundingRequired,
            };
        }

        // an errored balance reads as null, not 0, so only a real zero lands here.
        if (!isBalanceLoading && numericBalance === 0) {
            return { status: 'unfunded' };
        }

        return { status: 'ready' };
    }, [
        authorityMethod,
        isRealMode,
        walletMissing,
        isCustodialWalletMissing,
        isSocialAuthed,
        displayName,
        needsReactivation,
        builderApprovalStale,
        bootstrapRequired,
        agentAddress,
        validUntil,
        isFundingBlockedByBackend,
        isBalanceLoading,
        numericBalance,
    ]);
}
